/*
  Mmelt branching product quiz.

  One engine for all three quiz placements. Each section supplies its own
  markup and CSS and marks the pieces the engine drives with data-mq-*
  attributes, so the look stays per-section while the logic lives here.

  Required inside the root element:
    [data-mq-view="intro"|"question"|"result"]   views, toggled with .is-active
    [data-mq-meta] [data-mq-question] [data-mq-answers]   question view
    [data-mq-results]                            result view
  Optional:
    [data-mq-start] [data-mq-back] [data-mq-retake] [data-mq-progress]
*/
(function () {
  function init(root) {
    if (!root || root.dataset.mqReady === 'true') return;
    const uid = root.dataset.mqUid;
    const dataEl = document.getElementById('mq-data-' + uid);
    if (!dataEl) return;

    let db;
    try {
      db = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('Mmelt quiz: bad JSON', e);
      return;
    }
    root.dataset.mqReady = 'true';

    const views = {};
    root.querySelectorAll('[data-mq-view]').forEach((el) => {
      views[el.dataset.mqView] = el;
    });

    const elMeta = root.querySelector('[data-mq-meta]');
    const elQuestion = root.querySelector('[data-mq-question]');
    const elAnswers = root.querySelector('[data-mq-answers]');
    const elProgress = root.querySelector('[data-mq-progress]');
    const elResults = root.querySelector('[data-mq-results]');
    const btnBack = root.querySelector('[data-mq-back]');

    // history of node ids, last entry is the node on screen
    let path = [];

    const show = (name) => {
      Object.keys(views).forEach((k) => views[k].classList.toggle('is-active', k === name));
      const top = root.getBoundingClientRect().top + window.pageYOffset - 90;
      if (window.pageYOffset > top) window.scrollTo({ top: top, behavior: 'smooth' });
    };

    // longest number of questions still ahead, used for the progress bar
    const depthFrom = (nodeId, guard) => {
      guard = guard || 0;
      const node = db.nodes[nodeId];
      if (!node || guard > 12) return 0;
      let deepest = 0;
      node.a.forEach((ans) => {
        if (ans.go) deepest = Math.max(deepest, depthFrom(ans.go, guard + 1));
      });
      return 1 + deepest;
    };

    const drawProgress = () => {
      if (!elProgress) return;
      const done = path.length;
      const total = done + depthFrom(path[path.length - 1]) - 1;
      const pct = total > 0 ? Math.round((done / Math.max(total, 1)) * 100) : 100;
      const bars = elProgress.querySelectorAll('.q-progress-bar, .progress-segment');
      if (bars.length) {
        bars.forEach((bar, i) => bar.classList.toggle('is-filled', i < Math.round((pct / 100) * bars.length)));
      }
      const fill = elProgress.querySelector('.fp-q-fill, .q-progress-fill');
      if (fill) fill.style.width = pct + '%';
      elProgress.setAttribute('aria-valuenow', String(pct));
    };

    const renderNode = (nodeId) => {
      const node = db.nodes[nodeId];
      if (!node) return;

      if (elMeta) elMeta.textContent = String(path.length).padStart(2, '0') + ' · ' + (node.meta || '');
      if (elQuestion) elQuestion.textContent = node.q;
      if (btnBack) btnBack.style.display = path.length > 1 ? '' : 'none';

      // each section styles its answers with its own class names
      const cls = elAnswers.dataset.mqAnswerClass || '';
      const clsTitle = elAnswers.dataset.mqAnswerTitleClass || '';
      const clsNote = elAnswers.dataset.mqAnswerNoteClass || '';
      // sections that colour answers on hover in JS rather than CSS
      const palette = (elAnswers.dataset.mqAnswerColors || '')
        .split(',').map((c) => c.trim()).filter(Boolean);

      elAnswers.innerHTML = '';
      node.a.forEach((ans, i) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = ('mq-answer ' + cls).trim();
        btn.innerHTML =
          '<span class="' + ('mq-answer-title ' + clsTitle).trim() + '"></span>' +
          (ans.n ? '<span class="' + ('mq-answer-note ' + clsNote).trim() + '"></span>' : '');
        btn.querySelector('.mq-answer-title').textContent = ans.t;
        if (ans.n) btn.querySelector('.mq-answer-note').textContent = ans.n;

        if (palette.length) {
          const hover = palette[i % palette.length];
          btn.addEventListener('mouseenter', () => {
            btn.style.background = hover;
            btn.style.borderColor = hover;
            btn.style.color = '#111';
          });
          btn.addEventListener('mouseleave', () => {
            btn.style.background = '';
            btn.style.borderColor = '';
            btn.style.color = '';
          });
        }
        btn.addEventListener('click', () => {
          if (ans.go) {
            path.push(ans.go);
            renderNode(ans.go);
            show('question');
          } else if (ans.r) {
            renderResult(ans.r);
            show('result');
          }
        });
        elAnswers.appendChild(btn);
      });

      drawProgress();
    };

    // exact key, then case-insensitive, then match on the flavour half alone
    const lookup = (key) => {
      if (db.flavors[key]) return db.flavors[key];
      const lower = key.toLowerCase();
      const keys = Object.keys(db.flavors);
      let hit = keys.find((k) => k.toLowerCase() === lower);
      if (hit) return db.flavors[hit];
      const wanted = lower.split('::')[1] || lower;
      hit = keys.find((k) => {
        const f = (k.split('::')[1] || '').toLowerCase();
        return f.indexOf(wanted) === 0 || wanted.indexOf(f) === 0;
      });
      return hit ? db.flavors[hit] : null;
    };

    const renderResult = (keys) => {
      if (!elResults) return;
      const picks = keys.map(lookup).filter(Boolean);
      elResults.innerHTML = '';
      elResults.classList.toggle('mq-results--pair', picks.length > 1);

      if (!picks.length) {
        elResults.innerHTML =
          '<p class="mq-empty">That flavour is not in the catalogue right now. ' +
          '<a href="/collections/all">Browse everything</a> instead.</p>';
        return;
      }

      picks.forEach((p) => {
        const card = document.createElement('a');
        card.className = 'mq-card';
        card.href = p.url;
        card.innerHTML =
          '<div class="mq-card-img" style="background-color:' + p.color + '">' +
          (p.img ? '<img src="' + p.img + '" alt="" loading="lazy">' : '') +
          (p.avail ? '' : '<span class="mq-card-flag">Back soon</span>') +
          '</div>' +
          '<div class="mq-card-body">' +
          '<span class="mq-card-eyebrow"></span>' +
          '<h3 class="mq-card-title"></h3>' +
          '<p class="mq-card-meta"></p>' +
          '<span class="mq-card-cta">View this flavour &rarr;</span>' +
          '</div>';
        card.querySelector('.mq-card-eyebrow').textContent = p.line;
        card.querySelector('.mq-card-title').textContent = p.flavor;
        card.querySelector('.mq-card-meta').textContent = [p.size, p.price].filter(Boolean).join(' · ');
        elResults.appendChild(card);
      });

      if (picks.length > 1) {
        const or = document.createElement('span');
        or.className = 'mq-or';
        or.textContent = 'or';
        elResults.insertBefore(or, elResults.children[1]);
      }
    };

    const start = () => {
      path = [db.start];
      renderNode(db.start);
      show('question');
    };

    const reset = () => {
      path = [];
      show(views.intro ? 'intro' : 'question');
      if (!views.intro) start();
    };

    root.querySelectorAll('[data-mq-start]').forEach((b) => b.addEventListener('click', start));
    root.querySelectorAll('[data-mq-retake]').forEach((b) => b.addEventListener('click', reset));
    if (btnBack) {
      btnBack.addEventListener('click', () => {
        path.pop();
        if (!path.length) return reset();
        renderNode(path[path.length - 1]);
        show('question');
      });
    }

    show(views.intro ? 'intro' : 'question');
    if (!views.intro) start();
  }

  const boot = () => document.querySelectorAll('[data-mq-root]').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // theme editor re-renders a section without a page load
  document.addEventListener('shopify:section:load', boot);
})();
